import { CheckCircle2, XCircle, Loader2, Clock, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NodeIcon } from '@/components/ui/node-icon';
import { getNodeDefinition } from '@/utils/nodeDefinitions';
import type { NodeExecution } from './useLiveExecution';

interface ExecutedNodesListProps {
  nodes: NodeExecution[];
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
}

export function ExecutedNodesList({
  nodes,
  selectedNodeId,
  onSelectNode,
}: ExecutedNodesListProps) {
  const formatDuration = (node: NodeExecution) => {
    // For running nodes, show elapsed time
    if (node.status === 'running') {
      const elapsed = Date.now() - node.startedAt;
      if (elapsed < 1000) return `${elapsed}ms`;
      return `${(elapsed / 1000).toFixed(1)}s`;
    }

    // For completed nodes, use the duration or calculate from timestamps
    const duration = node.duration || (node.completedAt ? node.completedAt - node.startedAt : 0);

    if (!duration || duration <= 0) {
      return '< 1ms';
    }

    if (duration < 1000) return `${Math.round(duration)}ms`;
    if (duration < 60000) return `${(duration / 1000).toFixed(1)}s`;
    const mins = Math.floor(duration / 60000);
    const secs = Math.floor((duration % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  const getStatusIcon = (status: NodeExecution['status'], isSubNode: boolean = false) => {
    const sizeClass = isSubNode ? 'w-3 h-3' : 'w-4 h-4';
    switch (status) {
      case 'success':
        return <CheckCircle2 className={cn(sizeClass, 'text-green-500 flex-shrink-0')} />;
      case 'failed':
        return <XCircle className={cn(sizeClass, 'text-red-500 flex-shrink-0')} />;
      case 'running':
        return <Loader2 className={cn(sizeClass, 'text-blue-500 animate-spin flex-shrink-0')} />;
      default:
        return <Clock className={cn(sizeClass, 'text-muted-foreground flex-shrink-0')} />;
    }
  };

  if (nodes.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center text-muted-foreground text-sm">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 opacity-50" />
          <p>Waiting for nodes to execute...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 w-full"
      style={{ contain: 'strict', contentVisibility: 'auto' }}
    >
      <div className="p-2 space-y-1 w-full overflow-hidden">
        {nodes.map((node) => {
          const nodeDef = getNodeDefinition(node.nodeType);
          const isSelected = selectedNodeId === node.id;
          const isSubNode = node.isSubNode === true;

          // Sub-node (tool execution) styling
          if (isSubNode) {
            // Try to get the node definition for the tool's node type for proper icon
            // Fall back to nodeDef (from nodeType) which is also set to the tool type for sub-nodes
            const toolNodeDef = node.toolNodeType
              ? getNodeDefinition(node.toolNodeType)
              : (nodeDef.type !== 'tool' && nodeDef.icon ? nodeDef : null);
            const hasToolIcon = toolNodeDef && toolNodeDef.icon && toolNodeDef.type !== 'tool';

            // Generate a friendly display name for the tool
            const getToolDisplayName = () => {
              // If we have a label, use it (already cleaned up on backend)
              if (node.nodeLabel && !node.nodeLabel.includes('_')) {
                return node.nodeLabel;
              }
              // If we have a toolName, try to clean it up
              if (node.toolName) {
                // Remove node ID suffixes (e.g., "kb_search_WycmBAKROrWVuvM0r4iXk" -> "KB Search")
                const cleanName = node.toolName.replace(/_[A-Za-z0-9]{10,}$/, '');
                // Convert snake_case to Title Case
                return cleanName
                  .split('_')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ');
              }
              // Fall back to node type with similar formatting (use toolNodeType or nodeType)
              const typeForDisplay = node.toolNodeType || (node.nodeType !== 'tool' ? node.nodeType : null);
              if (typeForDisplay) {
                return typeForDisplay
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/-/g, ' ')
                  .trim()
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ');
              }
              return 'Tool';
            };

            return (
              <button
                key={node.id}
                onClick={() => onSelectNode(node.id)}
                className={cn(
                  'w-full text-left py-1.5 px-2 rounded-md transition-all hover:bg-accent/50 overflow-hidden box-border block',
                  'ml-4 border-l-2 border-muted-foreground/20',
                  isSelected && 'bg-accent/50 border-l-primary'
                )}
                style={{ contain: 'layout style', maxWidth: 'calc(100% - 16px)' }}
              >
                <div className="flex items-center gap-1.5 min-w-0 w-full overflow-hidden">
                  {getStatusIcon(node.status, true)}
                  <div
                    className="w-5 h-5 min-w-[20px] max-w-[20px] rounded flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{ backgroundColor: hasToolIcon ? toolNodeDef.bgColor : 'var(--accent-purple-100)' }}
                  >
                    {hasToolIcon ? (
                      <NodeIcon
                        icon={toolNodeDef.icon}
                        iconType={toolNodeDef.iconType}
                        iconColor={toolNodeDef.iconColor}
                        color={toolNodeDef.color}
                        className="w-2.5 h-2.5"
                      />
                    ) : (
                      <Wrench className="w-2.5 h-2.5 text-accent-purple-600 dark:text-accent-purple-400" />
                    )}
                  </div>
                  <span className="font-medium text-xs flex-1 min-w-0 truncate block text-muted-foreground">
                    {getToolDisplayName()}
                  </span>
                  <span className="text-[10px] text-muted-foreground/70 flex-shrink-0">
                    {formatDuration(node)}
                  </span>
                </div>
              </button>
            );
          }

          // Regular node styling
          return (
            <button
              key={node.id}
              onClick={() => onSelectNode(node.id)}
              className={cn(
                'w-full text-left p-2 rounded-lg transition-all hover:bg-accent overflow-hidden box-border block',
                isSelected && 'bg-accent border border-primary'
              )}
              style={{ contain: 'layout style', maxWidth: '100%' }}
            >
              <div className="flex items-center gap-2 min-w-0 w-full overflow-hidden">
                {getStatusIcon(node.status)}
                <div
                  className="w-6 h-6 min-w-[24px] max-w-[24px] rounded flex items-center justify-center flex-shrink-0 overflow-hidden"
                  style={{ backgroundColor: nodeDef.bgColor }}
                >
                  <NodeIcon
                    icon={nodeDef.icon}
                    iconType={nodeDef.iconType}
                    iconColor={nodeDef.iconColor}
                    color={nodeDef.color}
                    className="w-3 h-3"
                  />
                </div>
                <span className="font-medium text-sm flex-1 min-w-0 truncate block">
                  {node.nodeLabel || node.nodeType}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground pl-8 overflow-hidden">
                {node.status === 'running' ? (
                  <span className="text-blue-500 truncate">Running... {formatDuration(node)}</span>
                ) : (
                  <span className="truncate">{formatDuration(node)}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
